import { forwardRef, type MouseEvent } from 'react';
import NextLink from 'next/link';

import { trackDeferred, useAnalytics } from '#/lib/segment';

export const Link = forwardRef<
  HTMLAnchorElement,
  {
    href?: string | null;
    children: React.ReactNode;
    [key: string]: any;
  }
>(({ href, children, onClick, ...props }, ref) => {
  const analytics = useAnalytics();

  const isExternal =
    href?.startsWith('http') ||
    href?.startsWith('mailto') ||
    href?.startsWith('tel');

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (href) {
      // Keep Segment off the interaction critical path so INP isn't
      // inflated by analytics work before the next paint.
      trackDeferred(analytics, 'Link Clicked', {
        href,
        text: typeof children === 'string' ? children : undefined,
        isExternal,
      });
    }
    onClick?.(e);
  };

  if (!href) {
    return <span>{children}</span>;
  }

  if (isExternal) {
    return (
      <a href={href} ref={ref} onClick={handleClick} {...props}>
        {children}
      </a>
    );
  }

  return (
    <NextLink ref={ref} href={href} onClick={handleClick} {...props}>
      {children}
    </NextLink>
  );
});
Link.displayName = 'Link';
